import { Box, Stack } from '@mui/material';
import { useGetGuestJourneyActiveCardsQuery } from 'api/reports-service/guest-journey-hooks';
import {
  useGetGuestJourneyActiveDistributionsQuery,
  useGetGuestJourneyActiveGridQuery,
  useGetGuestJourneyActiveMemberTrendsQuery,
  useGetGuestJourneyActiveTrendsQuery,
} from 'api/reports-service/guest-journey-reports';
import IncDonutChart from 'components/charts/inc-donut-chart/inc-donut-chart.component';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import { useTranslation } from 'react-i18next';
import {
  locationsOnlyFilteredSearchParams,
  memberTrendsFilteredSearchParams,
  useActiveCustomersSearchParams,
} from '../guest-journey.hooks';
import ReportFieldsLink from '../report-fields/report-fields-link.component';
import ActiveCustomersAcquisitionSource from './active-customers-acquisition-source.component';
import ActiveCustomersGrid from './active-customers-grid.component';
import ActiveCustomersSummaryCards from './active-customers-summary-cards.component';
import ActiveCustomersVioTrends from './active-customers-vio-trends.component';
import MemberCountTrends from './member-count-trends.component';

/**
 * Queries only send the params on this tab's filter bar (Locations); the
 * Member Count Trends chart additionally sends its chart-scoped date range.
 */
const filteredSearchParams = locationsOnlyFilteredSearchParams;

const SearchParamsWrapper = () => {
  const searchParams = useSearchParams(useActiveCustomersSearchParams());
  return (
    <SearchParamsProvider value={searchParams}>
      <ActiveCustomers />
    </SearchParamsProvider>
  );
};

const ActiveCustomers = () => {
  const { t } = useTranslation();

  const summary = useSearchQuery(useGetGuestJourneyActiveCardsQuery, {
    filteredSearchParams,
  });
  const vioTrends = useSearchQuery(useGetGuestJourneyActiveTrendsQuery, {
    filteredSearchParams,
  });
  const grid = useSearchQuery(useGetGuestJourneyActiveGridQuery, {
    filteredSearchParams,
  });
  const distributions = useSearchQuery(
    useGetGuestJourneyActiveDistributionsQuery,
    { filteredSearchParams },
  );
  const memberTrends = useSearchQuery(
    useGetGuestJourneyActiveMemberTrendsQuery,
    {
      filteredSearchParams: memberTrendsFilteredSearchParams,
    },
  );

  // Acquisition-source bars: BE series labels are display-ready. Series are
  // rendered grouped (side-by-side) per design — no shared `stack` key, which
  // is what makes @mui/x-charts group rather than stack them.
  const firstSourceSeries = (
    distributions.data?.acquisitionSource?.series ?? []
  ).map(series => ({
    label: series.label,
    data: series.data,
  }));

  const firstSourceLabels =
    distributions.data?.acquisitionSource?.xlabels ?? [];

  const spendSlices =
    distributions.data?.spendPie?.data?.map((slice, i) => ({
      id: i,
      label: slice.label,
      value: slice.value,
    })) ?? [];

  const visitsSlices =
    distributions.data?.visitsPie?.data?.map((slice, i) => ({
      id: i,
      label: slice.label,
      value: slice.value,
    })) ?? [];

  return (
    <Stack marginTop={2}>
      <MultipleSearchQueryProvider
        value={{
          queries: {
            summary,
            vioTrends,
            grid,
            distributions,
            memberTrends,
          },
        }}
      >
        <Stack spacing={2}>
          <SearchToolbar />

          <ReportFieldsLink tab="activeCustomers" />

          <ActiveCustomersSummaryCards
            data={summary.data}
            loading={summary.queryState.isFetching}
          />

          <ActiveCustomersVioTrends
            trends={vioTrends.data?.trends}
            loading={vioTrends.queryState.isFetching}
            data-testid="ActiveCustomers.VioTrends"
          />

          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xl: '1fr 1fr 1fr' }}
            gridAutoRows={{ xs: 500 }}
            data-testid="ActiveCustomers.GridSection"
          >
            <Box gridColumn={{ xl: 'span 2' }}>
              <ActiveCustomersGrid
                data={grid.data}
                loading={grid.queryState.isFetching}
              />
            </Box>
            <MemberCountTrends
              data={memberTrends.data}
              loading={memberTrends.queryState.isFetching}
            />
          </Box>

          <Box
            display="grid"
            gap={2}
            gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr 1fr' }}
            gridAutoRows={{ xs: 380 }}
          >
            <IncDonutChart
              data-testid="ActiveCustomers.SpendDonut"
              title={t('page.guestJourney.activeCustomers.spendDonut.title')}
              data={spendSlices}
              showShare
              PieChartProps={{ loading: distributions.queryState.isFetching }}
            />
            <IncDonutChart
              data-testid="ActiveCustomers.VisitsDonut"
              title={t('page.guestJourney.activeCustomers.visitsDonut.title')}
              data={visitsSlices}
              showShare
              PieChartProps={{ loading: distributions.queryState.isFetching }}
            />
            <ActiveCustomersAcquisitionSource
              series={firstSourceSeries}
              xlabels={firstSourceLabels}
              loading={distributions.queryState.isFetching}
            />
          </Box>
        </Stack>
      </MultipleSearchQueryProvider>
    </Stack>
  );
};

export default SearchParamsWrapper;
